import { ENGAGEMENT_SCORES_COLORS } from 'constants/firmographics'
import moment from 'moment'
import { identity, isEmpty } from 'ramda'
import spacetime from 'spacetime'

import { DDSIconDefinition } from '@driftt/dds'
import { faLinkedin } from '@driftt/dds.icons/brands/faLinkedin'
import { faArrowPointer } from '@driftt/dds.icons/light/faArrowPointer'
import { faBullseyePointer } from '@driftt/dds.icons/light/faBullseyePointer'
import { faCalendarCheck } from '@driftt/dds.icons/light/faCalendarCheck'
import { faCirclePlay } from '@driftt/dds.icons/light/faCirclePlay'
import { faComment } from '@driftt/dds.icons/light/faComment'
import { faCommentSms } from '@driftt/dds.icons/light/faCommentSms'
import { faEnvelope } from '@driftt/dds.icons/light/faEnvelope'
import { faEnvelopeOpen } from '@driftt/dds.icons/light/faEnvelopeOpen'
import { faEye } from '@driftt/dds.icons/light/faEye'
import { faFiles } from '@driftt/dds.icons/light/faFiles'
import { faFileSignature } from '@driftt/dds.icons/light/faFileSignature'
import { faMagnifyingGlass } from '@driftt/dds.icons/light/faMagnifyingGlass'
import { faMobile } from '@driftt/dds.icons/light/faMobile'
import { faPencil } from '@driftt/dds.icons/light/faPencil'
import { faPenToSquare } from '@driftt/dds.icons/light/faPenToSquare'
import { faPhone } from '@driftt/dds.icons/light/faPhone'
import { faUser } from '@driftt/dds.icons/light/faUser'
import { faUserMinus } from '@driftt/dds.icons/light/faUserMinus'
import { faUserPlus } from '@driftt/dds.icons/light/faUserPlus'

import config from 'config'
import { OrgUsersById, User } from 'context/orgUsersContext'
import {
  Activity,
  ActivityContext,
  ActivityForContact,
  ActivityType,
  LastTouch,
  LastTouchType,
  SalesloftSignal
} from 'modules/accountActivity/domain'

export const getAgentDisplayName = (user: User | undefined) => {
  return user?.name || user?.email || 'Drift User'
}

export const getMeetingDateStr = (startTime: number | undefined) =>
  startTime ? moment(startTime).format('MMMM Do[,] YYYY') : ''

export const getMeetingTimeStr = (startTime: number | undefined) =>
  startTime ? moment(startTime).format('h:mm A') : ''

const formatTime = (startTime: number) => moment(startTime).format('MMMM Do YYYY [at] h:mm A')

const getMeetingDateTimeStr = (context: ActivityContext) =>
  context?.slotStart ? ` for ${formatTime(context?.slotStart)}` : ''

export const getSignalString = (context: ActivityContext) => {
  const signalString = context?.signal ? formatSignal(context?.signal) : 'Salesloft signal received'
  return signalString.length > 0 ? signalString : 'Salesloft signal received'
}
const formatSignal = (signal: SalesloftSignal) => {
  const descriptions = [signal, ...signal.indicators]
    ?.map(indicator => indicator?.description)
    .filter(desc => desc != null)
    ?.join('|||')
  return descriptions
}

const getParticipantsStr = (participantIds: number[] | undefined, orgUsersById: OrgUsersById) => {
  const agents = participantsToString(participantIds, orgUsersById)
  return !isEmpty(agents) ? ` with ${agents}` : ''
}

const getPercentageStr = (context: ActivityContext) => {
  const percentage = context.percentViewed
  return percentage ? ` ${percentage}% of` : ''
}

export const participantsToString = (
  participantIds: number[] | undefined,
  orgUsersById: OrgUsersById
) => {
  return participantIds
    ?.map(id => getAgentDisplayName(orgUsersById[id]))
    ?.filter(identity)
    ?.join(', ')
}

const isRecentUserActivity = (createdAt: number) => {
  const {
    diff: { years, months, days, hours, minutes }
  } = spacetime.now().since(createdAt)

  return minutes < 4 && years === 0 && months === 0 && days === 0 && hours === 0 // there is no science behind this number. plan is to replace this with presence data.
}

export const userActivityTypeToPreviewSnippet = (
  activity: Activity | ActivityForContact,
  orgUsersById: OrgUsersById
): {
  action: string
  icon: DDSIconDefinition
  preview?: string
  suffix?: string
  href?: string
} => {
  const context = activity.activityContext
  const sfdcTaskSubject = context.driftSfdcTask?.subject
  switch (activity.activityType) {
    case ActivityType.PAGE_VIEW:
      return {
        icon: faArrowPointer,
        action: 'viewed',
        preview: context.pageTitle || context.pageUrl,
        href: prefixUrlWithHttp(context.pageUrl)
      }
    case ActivityType.MEETING_BOOKED:
      return {
        icon: faCalendarCheck,
        action: `booked a meeting`,
        suffix: `${getParticipantsStr(
          context.agentParticipants,
          orgUsersById
        )}${getMeetingDateTimeStr(context)}`
      }
    case ActivityType.CONVERSATION:
      return {
        icon: faComment,
        action: context.conversationPreview ? 'said' : 'conversation',
        preview: context.conversationPreview,
        suffix: context.conversationPreview
          ? 'in a conversation' + getParticipantsStr(context.participantIds, orgUsersById)
          : '',
        href: `${config.BASE_URL}/conversations/${context.id}`
      }
    case ActivityType.EMAIL_CLICK:
      return {
        icon: faArrowPointer,
        action: 'clicked link in email',
        preview: context.subject
      }
    case ActivityType.EMAIL_OPEN:
      return {
        icon: faEnvelopeOpen,
        action: 'opened email',
        preview: context.subject
      }
    case ActivityType.VIDEO:
      return {
        icon: faCirclePlay,
        action:
          (isRecentUserActivity(activity.createdAt) ? `is watching` : `watched`) +
          getPercentageStr(context),
        preview: context.title,
        href: context.url
      }
    case ActivityType.MEETING_RECORDED:
      return {
        icon: faCalendarCheck,
        action: 'recorded a meeting'
      }
    case ActivityType.EMAIL_CLICK_SFDC:
      return {
        icon: faCalendarCheck,
        action: 'clicked link in email',
        preview: sfdcTaskSubject
      }
    case ActivityType.EMAIL_OPEN_SFDC:
      return {
        icon: faEnvelopeOpen,
        action: 'opened email',
        preview: sfdcTaskSubject
      }
    case ActivityType.EMAIL_INBOUND:
      return {
        icon: faEnvelope,
        action: 'sent an email',
        preview: sfdcTaskSubject
      }
    case ActivityType.WEBINAR:
      return {
        icon: faUserPlus,
        action: 'had a webinar activity',
        preview: sfdcTaskSubject
      }
    case ActivityType.FILLED_OUT_FORM:
      return {
        icon: faPencil,
        action: 'filled out form',
        preview: sfdcTaskSubject
      }
    case ActivityType.OTHER_CHATBOT_CONVERSATION:
      return {
        icon: faComment,
        action: 'said',
        preview: sfdcTaskSubject
      }
    case ActivityType.SMS_INBOUND:
      return {
        icon: faCommentSms,
        action: 'sent an SMS',
        preview: sfdcTaskSubject
      }
    case ActivityType.UNSUBSCRIBED:
      return {
        icon: faEnvelope,
        action: 'unsubscribed from emails',
        preview: sfdcTaskSubject
      }
    case ActivityType.END_USER_IDENTIFIED:
      return {
        icon: faArrowPointer,
        action: 'was identified'
      }
    case ActivityType.SIGNAL:
      return {
        icon: faArrowPointer,
        action: '',
        suffix: getSignalString(context).split('|||')[0]
      }
    default:
      return {
        icon: faArrowPointer,
        action: `${activity.activityType} activity`
      }
  }
}

export const formatLastTouched = (lastTouch: LastTouch | null | undefined) => {
  if (!lastTouch)
    return {
      showBlank: true
    }

  const senderAndRecipient = {
    sender: lastTouch.ownerName || lastTouch.ownerEmail,
    recipient: lastTouch?.buyer?.name || lastTouch?.buyer?.email
  }

  if (
    lastTouch.taskCategory === LastTouchType.EMAIL_SEND ||
    lastTouch.taskCategory === LastTouchType.EMAIL_OUTBOUND ||
    lastTouch.taskCategory === LastTouchType.AUTOMATED_ACCOUNT_REMINDER
  ) {
    return {
      ...senderAndRecipient,
      icon: faEnvelope,
      action: 'sent an',
      actionType:
        lastTouch.taskCategory === LastTouchType.EMAIL_OUTBOUND ? 'outbound email' : 'email',
      union: 'to'
    }
  }
  if (lastTouch.taskCategory === LastTouchType.SMS_OUTBOUND) {
    return {
      ...senderAndRecipient,
      icon: faMobile,
      action: 'sent an',
      actionType: 'SMS',
      union: 'to'
    }
  }
  if (lastTouch.taskCategory === LastTouchType.CALL_TASK) {
    return {
      ...senderAndRecipient,
      icon: faPhone,
      action: '',
      actionType: 'called',
      union: ''
    }
  }
  if (lastTouch.taskCategory === LastTouchType.LINKED_IN_MESSAGE) {
    return {
      ...senderAndRecipient,
      icon: faLinkedin,
      action: 'sent a',
      actionType: 'LinkedIn message',
      union: 'to'
    }
  }
  if (lastTouch.taskCategory === LastTouchType.LINKED_IN_CONNECTION_REQUEST) {
    return {
      ...senderAndRecipient,
      icon: faLinkedin,
      action: 'sent a',
      actionType: 'LinkedIn connection request',
      union: 'to'
    }
  }
  if (lastTouch.taskCategory === LastTouchType.OTHER_CHATBOT_CONVERSATION) {
    return {
      ...senderAndRecipient,
      icon: faComment,
      action: 'had a',
      actionType: 'Drift conversation',
      union: 'with'
    }
  }

  return {
    showBlank: true
  }
}

export const prefixUrlWithHttp = (link?: string): string | undefined => {
  if (!link) return link

  const VALID_PREFIXES = ['http://', 'https://', 'mailto:']

  for (const prefix of VALID_PREFIXES) {
    if (link.startsWith(prefix)) {
      return link
    }
  }

  return `http://${link}`
}

export const getEngagementScoreColor = (score: number) => {
  return ENGAGEMENT_SCORES_COLORS[score as keyof typeof ENGAGEMENT_SCORES_COLORS]
}

export const getActivityEventIconAndColor = (
  type: ActivityType
): {
  icon: DDSIconDefinition
  color: string
  tooltip?: string
} => {
  switch (type) {
    case ActivityType.PAGE_VIEW:
      return { icon: faEye, color: '$green6', tooltip: 'Page view' }
    case ActivityType.CONVERSATION:
      return { icon: faComment, color: '$blue8', tooltip: 'Drift chat' }
    case ActivityType.MEETING_BOOKED:
    case ActivityType.MEETING_CANCELED:
    case ActivityType.MEETING_RESCHEDULED:
    case ActivityType.MEETING_RECORDED:
      return { icon: faCalendarCheck, color: '$red6', tooltip: 'Meeting' }
    case ActivityType.VIDEO:
      return { icon: faCirclePlay, color: '$orange6', tooltip: 'Video view' }
    case ActivityType.EMAIL_CLICK:
    case ActivityType.EMAIL_CLICK_SFDC:
    case ActivityType.EMAIL_OPEN:
    case ActivityType.EMAIL_OPEN_SFDC:
    case ActivityType.EMAIL_SEND:
    case ActivityType.EMAIL_INBOUND:
      return { icon: faEnvelope, color: '$indigo9', tooltip: 'Email' }
    case ActivityType.WEBINAR:
      return { icon: faBullseyePointer, color: '$pink6', tooltip: 'Webinar' }
    case ActivityType.FILLED_OUT_FORM:
      return { icon: faPenToSquare, color: '$yellow6', tooltip: 'Filled out form' }
    case ActivityType.UNSUBSCRIBED:
      return { icon: faUserMinus, color: '$gray6', tooltip: 'Unsubscribed' }
    case ActivityType.END_USER_IDENTIFIED:
      return { icon: faUserPlus, color: '$cyan6', tooltip: 'End user identified' }
    case ActivityType.SMS_INBOUND:
      return { icon: faCommentSms, color: '$lime6', tooltip: 'SMS' }
    case ActivityType.OTHER_CHATBOT_CONVERSATION:
      return { icon: faComment, color: '$blue8', tooltip: 'Other chatbot conversation' }
    case ActivityType.CONTRACT_REQUEST:
      return { icon: faFileSignature, color: '$purple6', tooltip: 'Contract request' }
    case ActivityType.WIDGET_CONTENT_CLICK:
      return { icon: faFiles, color: '$pink8', tooltip: 'Clicked content recommendation' }
    case ActivityType.WIDGET_SEARCH_SEND:
      return { icon: faMagnifyingGlass, color: '$indigo9', tooltip: 'Search' }

    default:
      return { icon: faUser, color: '$green6', tooltip: 'End user' }
  }
}
